import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Modal } from 'flowbite';
import { LiteralService, StorageService, UsersService, UtilsService } from 'src/app/services';
import { VehiclesService } from 'src/app/services/vehicles/vehicles.service';
import { NotificationTypes, UserRole, UserSortType, UserStatus, UserType } from 'src/app/models/enums';
import { APP } from 'src/app/config/app';
import { images } from 'src/images';

export interface IDepartureRow {
  date: string;
  startTime: string;
  vehicleId?: any;
  driverId?: any;
  driverName?: string;
  vehicleName?: string;
}

export interface IDeparturesPayload {
  departures: { departureDateTime: string; vehicleId: number; driverId: number; vehicleName: string; driverName: string }[];
  setupId?: number;
}

@Component({
    selector: 'app-modal-route-departures',
    templateUrl: './modal-route-departures.component.html',
    styleUrl: './modal-route-departures.component.scss',
    standalone: false
})
export class ModalRouteDeparturesComponent implements OnChanges {

  @Input() id: string;
  @Input() modal: Modal;
  @Input() hidden: boolean;
  @Input() loading = false;
  @Input() clientId?: number;
  @Input() setups: { value: string; label: string }[] = [];
  @Output() action = new EventEmitter<IDeparturesPayload>();

  public selectedSetupId?: string;

  public images = images;
  public rows: IDepartureRow[] = [this.newRow()];
  public createdDrivers: { value: any; label: string }[] = [];
  public createdVehicles: { value: any; label: string }[] = [];
  public operators: { value: string; label: string }[] = [];
  public creatingResource = false;
  public showNewVehicle = false;
  public showNewDriver = false;
  public newVehicle: { name: string; availableSeats?: number; operatorId?: string } = { name: '' };
  public newDriver: { firstName: string; lastName: string; operatorId?: string } = { firstName: '', lastName: '' };

  private loadedClientId?: number;

  constructor(public literalService: LiteralService,
              private vehiclesService: VehiclesService,
              private usersService: UsersService,
              private storageService: StorageService,
              private utilsService: UtilsService) { }

  ngOnChanges() {
    if (this.clientId != null && this.clientId !== this.loadedClientId) {
      this.loadedClientId = this.clientId;
      this.loadClientResources();
    }
  }

  private async loadClientResources() {
    // Vehicles, drivers and operators are restricted to the operators of the service's client
    try {
      const operators: any = await this.usersService.getOperatorsByClient([this.clientId!]);
      this.operators = (operators ?? []).map((operator: any) => ({ value: String(operator.id), label: operator.name }));
    } catch {
      this.operators = [];
    }
  }


  private newRow(): IDepartureRow {
    return { date: '', startTime: '' };
  }

  addDeparture() {
    this.rows.push(this.newRow());
  }

  removeDeparture(index: number) {
    if (this.rows.length > 1) this.rows.splice(index, 1);
  }

  onVehicleSelected(row: IDepartureRow, vehicle: { value: any; label: string }) {
    row.vehicleId = vehicle.value;
    row.vehicleName = vehicle.label;
  }

  onDriverSelected(row: IDepartureRow, driver: { value: any; label: string }) {
    row.driverId = driver.value;
    row.driverName = driver.label;
  }

  onNewVehicleOperatorSelected(event: any) {
    this.newVehicle.operatorId = String(event?.value ?? event);
  }

  onNewDriverOperatorSelected(event: any) {
    this.newDriver.operatorId = String(event?.value ?? event);
  }

  isRowValid(row: IDepartureRow): boolean {
    return !!row.date && !!row.startTime && !!row.vehicleId && !!row.driverId;
  }

  get isValid(): boolean {
    if (this.setups.length > 0 && !this.selectedSetupId) return false;
    return this.rows.length >= 1 && this.rows.every((row) => this.isRowValid(row));
  }

  onSetupSelected(event: any) {
    this.selectedSetupId = String(event?.value ?? event);
  }

  get isNewVehicleValid(): boolean {
    return !!this.newVehicle.name && this.newVehicle.availableSeats != null && this.newVehicle.availableSeats > 0 && !!this.newVehicle.operatorId;
  }

  get isNewDriverValid(): boolean {
    return !!this.newDriver.firstName && !!this.newDriver.lastName && !!this.newDriver.operatorId;
  }

  async submitNewVehicle() {
    if (!this.isNewVehicleValid) return;
    this.creatingResource = true;
    try {
      const vehicle: any = await this.vehiclesService.createVehicle({
        name: this.newVehicle.name.trim(),
        availableSeats: this.newVehicle.availableSeats!,
        operatorId: Number(this.newVehicle.operatorId),
      });
      this.createdVehicles = [...this.createdVehicles, { value: vehicle.id, label: vehicle.name }];
      this.newVehicle = { name: '' };
      this.showNewVehicle = false;
      this.utilsService.showNotification(images.notifications.check, 'services.routes.departuresModal.vehicleCreated', NotificationTypes.SUCCESS);
    } catch {
      this.utilsService.showNotification(images.notifications.warning, 'services.routes.departuresModal.vehicleCreateError', NotificationTypes.DANGER);
    } finally {
      this.creatingResource = false;
    }
  }

  async submitNewDriver() {
    if (!this.isNewDriverValid) return;
    this.creatingResource = true;
    try {
      // Clients, town and language are not asked for: client comes from the wizard context and
      // the driver is registered under the logged admin's town and language
      const town: any = await this.usersService.getLoggedUserTown();
      if (!town?.id) throw new Error('no town available for the driver');
      const language = await this.storageService.getLanguage();
      const driver: any = await this.usersService.createUser({
        firstName: this.newDriver.firstName.trim(),
        lastName: this.newDriver.lastName.trim(),
        language: language,
        clients: [{ id: this.clientId }],
        town: { id: town.id },
        operator: { id: Number(this.newDriver.operatorId) },
        role: UserRole.USER,
        type: UserType.Driver,
      } as any);
      this.createdDrivers = [...this.createdDrivers, {
        value: driver.id,
        label: `${driver.firstName ?? this.newDriver.firstName} ${driver.lastName ?? this.newDriver.lastName}`.trim(),
      }];
      this.newDriver = { firstName: '', lastName: '' };
      this.showNewDriver = false;
      this.utilsService.showNotification(images.notifications.check, 'services.routes.departuresModal.driverCreated', NotificationTypes.SUCCESS);
    } catch {
      this.utilsService.showNotification(images.notifications.warning, 'services.routes.departuresModal.driverCreateError', NotificationTypes.DANGER);
    } finally {
      this.creatingResource = false;
    }
  }

  reset() {
    this.rows = [this.newRow()];
    this.showNewVehicle = false;
    this.showNewDriver = false;
    this.newVehicle = { name: '' };
    this.newDriver = { firstName: '', lastName: '' };
  }

  toggleNewDriver() {
    this.showNewDriver = !this.showNewDriver;
    this.showNewVehicle = false;
    this.newDriver = { firstName: '', lastName: '' };
  }

  toggleNewVehicle() {
    this.showNewVehicle = !this.showNewVehicle;
    this.showNewDriver = false;
    this.newVehicle = { name: '' };
  }

  close() {
    this.modal.hide();
    this.reset();
  }

  onSubmit() {
    if (!this.isValid) return;
    this.action.emit({
      departures: this.rows.map((row) => ({
        departureDateTime: `${row.date}T${row.startTime.length === 5 ? row.startTime + ':00' : row.startTime}`,
        vehicleId: Number(row.vehicleId),
        driverId: Number(row.driverId),
        vehicleName: row.vehicleName ?? '',
        driverName: row.driverName ?? '',
      })),
      setupId: this.selectedSetupId != null ? Number(this.selectedSetupId) : undefined,
    });
  }
}
